import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AgeingBucket,
  FinanceDrilldownKind,
  FinanceDrilldownRow,
  FinanceMovement,
  FinanceReportQuery,
  PayableSettlement,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Card } from '@/components/ui/Card';
import { formatMoney } from '@/modules/catalog/money';
import { useLocale } from '@/hooks/useLocale';
import { ExportCsvButton } from './ExportCsvButton';
import { useFinanceRecordsQuery } from '../api/reports.api';
import { MoneyByCurrencyText } from './MoneyByCurrencyText';

const PAGE_SIZE = 20;

export interface FinanceRecordsRequest {
  kind: FinanceDrilldownKind;
  movement?: FinanceMovement;
  bucket?: AgeingBucket;
  settlement?: PayableSettlement;
  /** What the figure is called, already translated. */
  title: string;
}

type Column = {
  key: string;
  label: string;
  end?: boolean;
  cell: (row: FinanceDrilldownRow) => ReactNode;
};

/**
 * The records behind one Finance figure.
 *
 * Paged, filtered and totalled by the server under the report's own scope, so the total shown is
 * what every matching record adds up to — the figure itself. A customer is named only when the
 * server sent the name; without customers.view it never does, and the column is left out.
 */
export function FinanceRecords({
  query,
  request,
  customerIdentity,
  onClose,
}: {
  query: FinanceReportQuery;
  request: FinanceRecordsRequest;
  customerIdentity: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [page, setPage] = useState(1);
  const recordsQuery = {
    ...query,
    kind: request.kind,
    movement: request.movement,
    bucket: request.bucket,
    settlement: request.settlement,
    page,
    pageSize: PAGE_SIZE,
  };
  const records = useFinanceRecordsQuery(recordsQuery);
  const pages = records.data ? Math.max(1, Math.ceil(records.data.total / PAGE_SIZE)) : 1;
  const headingId = `finance-records-${request.kind}-${request.movement ?? request.bucket ?? request.settlement ?? 'all'}`;

  const ltr = (value: ReactNode) => <span dir="ltr">{value ?? '—'}</span>;
  const money = (row: FinanceDrilldownRow, minor: number | null) =>
    minor === null ? '—' : ltr(formatMoney(minor, locale, row.currencyCode));
  const name = (primary: string | null, arabic: string | null) =>
    (locale === 'ar' && arabic) || primary || '—';
  const column = (
    key: string,
    cell: (row: FinanceDrilldownRow) => ReactNode,
    end = false,
  ): Column => ({ key, label: t(`reports.finance.columns.${key}`), end, cell });

  const reference = column('reference', (row) => ltr(row.reference));
  const order = column('order', (row) => ltr(row.orderReference));
  const date = column('date', (row) => ltr(row.businessDate));
  const due = column('dueDate', (row) => ltr(row.dueDate));
  const branch = column('branch', (row) => row.branchName);
  const method = column('method', (row) => (row.method ? t(`sales.methods.${row.method}`) : '—'));
  const customer = customerIdentity ? [column('customer', (row) => row.party ?? '—')] : [];
  const supplier = column('supplier', (row) => name(row.party, row.partyAr));
  const amount = column('amount', (row) => money(row, row.amountMinor), true);
  const overdue = column('daysOverdue', (row) => ltr(row.daysOverdue), true);
  const bucket = column('bucket', (row) =>
    row.bucket ? t(`reports.finance.buckets.${row.bucket}`) : '—',
  );

  const columns: Column[] =
    request.kind === 'COLLECTION_TRANSACTIONS'
      ? [
          date,
          order,
          ...customer,
          branch,
          method,
          column('movement', (row) =>
            row.movement ? t(`reports.finance.movement.${row.movement}`) : '—',
          ),
          amount,
        ]
      : request.kind === 'RECEIVABLE_ORDERS'
        ? [
            order,
            date,
            ...customer,
            branch,
            column('total', (row) => money(row, row.totalMinor), true),
            column('settled', (row) => money(row, row.settledMinor), true),
            amount,
          ]
        : request.kind === 'SCHEDULED_INSTALMENTS'
          ? [
              reference,
              order,
              column('sequence', (row) => ltr(row.sequence), true),
              due,
              overdue,
              bucket,
              ...customer,
              branch,
              amount,
            ]
          : request.kind === 'PAYABLE_INVOICES' || request.kind === 'SUPPLIER_INVOICES'
            ? [
                reference,
                column('supplierInvoice', (row) => ltr(row.orderReference)),
                supplier,
                branch,
                date,
                due,
                overdue,
                column('settlement', (row) =>
                  row.settlement ? t(`payables.settlement.${row.settlement}`) : '—',
                ),
                column('total', (row) => money(row, row.totalMinor), true),
                amount,
              ]
            : request.kind === 'SUPPLIER_PAYMENTS'
              ? [reference, date, supplier, branch, method, amount]
              : [
                  reference,
                  date,
                  column('category', (row) => name(row.categoryName, row.categoryNameAr)),
                  column('payee', (row) => name(row.party, row.partyAr)),
                  branch,
                  method,
                  amount,
                ];

  return (
    <Card className="min-w-0 p-4">
      <section aria-labelledby={headingId}>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h3 id={headingId} className="text-ink font-semibold">
            {request.title}
          </h3>
          <span className="flex flex-wrap items-baseline gap-3">
            <ExportCsvButton
              target={{
                method: 'GET',
                path: '/reports/finance/records/export',
                query: recordsQuery,
              }}
            />
            <button
              type="button"
              onClick={onClose}
              className="text-brand-700 text-sm font-medium hover:underline"
            >
              {t('reports.sales.records.close')}
            </button>
          </span>
        </div>
        {records.isError ? (
          <div className="mt-3">
            <Alert tone="error">{t('reports.sales.records.loadError')}</Alert>
          </div>
        ) : null}
        {records.isPending ? (
          <div
            className="bg-surface border-line mt-3 h-24 animate-pulse rounded-lg border"
            aria-hidden
          />
        ) : null}
        {records.data ? (
          <div className="text-muted mt-2 flex flex-wrap items-baseline gap-x-2 text-sm">
            <span>{t('reports.finance.records.totalOf', { count: records.data.total })}</span>
            <MoneyByCurrencyText amounts={records.data.totals} className="text-ink font-medium" />
          </div>
        ) : null}
        {records.data && records.data.rows.length === 0 ? (
          <p className="text-muted mt-3 text-sm">{t('reports.sales.records.empty')}</p>
        ) : null}
        {records.data && records.data.rows.length > 0 ? (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[40rem] text-sm [&_td]:px-2 [&_th]:px-2">
              <thead>
                <tr className="text-muted border-line border-b">
                  {columns.map((entry) => (
                    <th
                      key={entry.key}
                      className={`py-2 font-medium ${entry.end ? 'text-end' : 'text-start'}`}
                    >
                      {entry.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.data.rows.map((row) => (
                  <tr
                    key={`${row.kind}-${row.id}`}
                    className="border-line border-b last:border-b-0"
                  >
                    {columns.map((entry) => (
                      <td
                        key={entry.key}
                        className={`py-2 ${entry.end ? 'text-end tabular-nums' : ''}`}
                      >
                        {entry.cell(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {records.data && pages > 1 ? (
          <div className="mt-3 flex items-center justify-between gap-3 text-sm">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
              className="text-brand-700 font-medium hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('reports.sales.records.previous')}
            </button>
            <span className="text-muted">{t('reports.sales.records.page', { page, pages })}</span>
            <button
              type="button"
              disabled={page >= pages}
              onClick={() => setPage((current) => current + 1)}
              className="text-brand-700 font-medium hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('reports.sales.records.next')}
            </button>
          </div>
        ) : null}
      </section>
    </Card>
  );
}
